/* ==========================================================================
   CDP - Custom (Claude) Design Product Page
   Component scripts. Everything is a custom element, so the Theme Editor's
   section re-render (shopify:section:load / :unload) re-initialises and
   tears down automatically via connectedCallback / disconnectedCallback.
   No global listeners are registered on DOMContentLoaded.
   ========================================================================== */
(function () {
  'use strict';

  if (window.CDP && window.CDP.__loaded) return;
  var CDP = (window.CDP = window.CDP || {});
  CDP.__loaded = true;

  /* ---------------------------------------------------------------- utils */

  /* Formats money from cents. Fraction digits are left to Intl so each
     currency uses its own convention (ILS 2 digits, JPY 0, and so on). */
  function money(cents, el) {
    var root = el && el.closest ? el.closest('[data-cdp-currency]') : null;
    var currency = (root && root.dataset.cdpCurrency) || CDP.currency || 'USD';
    var locale = (root && root.dataset.cdpLocale) || CDP.locale || document.documentElement.lang || 'en';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }
  CDP.money = money;

  /* Applies a quantity-offer discount. Rounds the same way the Liquid side
     does, so the server-rendered price and the JS-updated price agree. */
  function applyDiscount(cents, percent) {
    if (!percent) return cents;
    return Math.round((cents * (100 - percent)) / 100);
  }

  function parseJSONScript(scope, selector) {
    var el = scope.querySelector(selector);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.warn('[cdp] could not parse', selector, e);
      return null;
    }
  }

  /* ============================================================ <cdp-product>
     The single source of truth for variant / quantity / selling-plan state.
     Everything else (sticky bar, final CTA, gallery) listens to it.
     ========================================================================= */
  if (!customElements.get('cdp-product')) {
    customElements.define(
      'cdp-product',
      class CdpProduct extends HTMLElement {
        connectedCallback() {
          this.sectionId = this.dataset.sectionId;
          this.variants = parseJSONScript(this, '[data-cdp-variants]') || [];
          this.form = this.querySelector('form[data-type="add-to-cart-form"]');
          this.idInput = this.querySelector('.cdp-variant-id');
          this.qtyInput = this.querySelector('[data-cdp-qty-input]');
          this.planInput = this.querySelector('[data-cdp-selling-plan]');
          this.priceIncludesQty = this.dataset.priceIncludesQuantity === 'true';

          var self = this;
          this.current =
            this.variants.filter(function (v) {
              return String(v.id) === String(self.idInput && self.idInput.value);
            })[0] ||
            this.variants[0] ||
            null;

          this._onChange = this.handleChange.bind(this);
          this.addEventListener('change', this._onChange);
          this._onClick = this.handleClick.bind(this);
          this.addEventListener('click', this._onClick);

          this._onExternal = this.handleExternalRequest.bind(this);
          document.addEventListener('cdp:request', this._onExternal);

          CDP.main = this;
          this.update({ updateUrl: false });
        }

        disconnectedCallback() {
          this.removeEventListener('change', this._onChange);
          this.removeEventListener('click', this._onClick);
          document.removeEventListener('cdp:request', this._onExternal);
          if (CDP.main === this) CDP.main = null;
        }

        /* ------------------------------------------------------------ input */

        handleChange(evt) {
          var t = evt.target;
          if (!t || !t.matches) return;
          if (t.matches('[data-cdp-option-index]')) {
            this.selectFromOptions();
            this.update({ updateUrl: true });
          } else if (t.matches('[data-cdp-offer]')) {
            this.setQuantity(parseInt(t.dataset.quantity, 10) || 1);
            this.update({});
          } else if (t.matches('[data-cdp-qty-input]')) {
            this.setQuantity(parseInt(t.value, 10) || 1);
            this.update({});
          } else if (t.matches('[data-cdp-selling-plan-input]')) {
            if (this.planInput) this.planInput.value = t.value || '';
            this.update({});
          }
        }

        handleClick(evt) {
          var step = evt.target.closest ? evt.target.closest('[data-cdp-qty-step]') : null;
          if (step) {
            evt.preventDefault();
            var dir = step.dataset.cdpQtyStep === 'up' ? 1 : -1;
            this.setQuantity(this.quantity + dir);
            this.update({});
          }
        }

        /* Requests coming from other sections (e.g. the final CTA). */
        handleExternalRequest(evt) {
          var d = evt.detail || {};
          if (d.quantity) {
            var radio = this.querySelector('[data-cdp-offer][data-quantity="' + d.quantity + '"]');
            if (radio) radio.checked = true;
            this.setQuantity(d.quantity);
            this.update({});
          }
          if (d.variantId) {
            var v = this.variants.filter(function (x) {
              return String(x.id) === String(d.variantId);
            })[0];
            if (v) {
              this.current = v;
              this.syncOptionInputs();
              this.update({ updateUrl: true });
            }
          }
          if (d.action === 'add') this.addToCart();
        }

        /* ------------------------------------------------------------- state */

        /* Discount % attached to the currently selected quantity offer. */
        get tierDiscount() {
          var sel = this.querySelector('[data-cdp-offer]:checked');
          return sel ? parseFloat(sel.dataset.discount) || 0 : 0;
        }

        get tiers() {
          return Array.prototype.map.call(this.querySelectorAll('[data-cdp-offer]'), function (i) {
            return { quantity: parseInt(i.dataset.quantity, 10) || 1, discount: parseFloat(i.dataset.discount) || 0 };
          });
        }

        /* The one place that decides what the current selection costs. */
        computePricing(v, qty) {
          if (!v) return null;
          var mult = this.priceIncludesQty ? qty : 1;
          var discount = this.priceIncludesQty ? this.tierDiscount : 0;
          var list = v.compare_at_price && v.compare_at_price > v.price ? v.compare_at_price : v.price;
          var total = applyDiscount(v.price * mult, discount);
          var compare = list * mult;
          return {
            total: total,
            compare: compare,
            onSale: compare > total,
            savePct: compare > total ? Math.round(((compare - total) / compare) * 100) : 0,
            unit: Math.round(total / mult),
            multiplier: mult,
            discount: discount
          };
        }

        get quantity() {
          return Math.max(1, parseInt(this.qtyInput ? this.qtyInput.value : 1, 10) || 1);
        }

        setQuantity(n) {
          if (!this.qtyInput) return;
          var min = parseInt(this.qtyInput.min, 10) || 1;
          var max = this.qtyInput.max ? parseInt(this.qtyInput.max, 10) : null;
          var step = parseInt(this.qtyInput.step, 10) || 1;
          n = Math.max(min, n);
          if (max) n = Math.min(max, n);
          if (step > 1) n = Math.max(min, Math.round(n / step) * step);
          this.qtyInput.value = n;
        }

        selectedOptionValues() {
          var values = [];
          this.querySelectorAll('[data-cdp-option-index]').forEach(function (input) {
            var idx = parseInt(input.dataset.cdpOptionIndex, 10);
            if (input.tagName === 'SELECT') values[idx] = input.value;
            else if (input.checked) values[idx] = input.value;
          });
          return values;
        }

        selectFromOptions() {
          var wanted = this.selectedOptionValues();
          if (!wanted.length) return;
          var match = this.variants.filter(function (v) {
            return wanted.every(function (val, i) {
              return val === undefined || v.options[i] === val;
            });
          })[0];
          this.current = match || null;
        }

        syncOptionInputs() {
          if (!this.current) return;
          var opts = this.current.options;
          this.querySelectorAll('[data-cdp-option-index]').forEach(function (input) {
            var idx = parseInt(input.dataset.cdpOptionIndex, 10);
            if (input.tagName === 'SELECT') input.value = opts[idx];
            else input.checked = input.value === opts[idx];
          });
        }

        /* Mark option values that cannot produce an available variant. */
        markUnavailableOptions() {
          var selected = this.selectedOptionValues();
          var variants = this.variants;
          this.querySelectorAll('[data-cdp-option-index]').forEach(function (input) {
            if (input.tagName === 'SELECT') return;
            var idx = parseInt(input.dataset.cdpOptionIndex, 10);
            var probe = selected.slice();
            probe[idx] = input.value;
            var ok = variants.some(function (v) {
              return (
                v.available &&
                probe.every(function (val, i) {
                  return val === undefined || v.options[i] === val;
                })
              );
            });
            var label = input.nextElementSibling;
            if (label) label.classList.toggle('cdp-unavailable', !ok);
            input.setAttribute('data-cdp-unavailable', ok ? 'false' : 'true');
          });
        }

        /* ------------------------------------------------------------ render */

        update(opts) {
          opts = opts || {};
          var v = this.current;
          var qty = this.quantity;

          if (this.idInput) {
            this.idInput.value = v ? v.id : '';
            this.idInput.disabled = !v || !v.available;
          }

          this.markUnavailableOptions();
          this.querySelectorAll('[data-cdp-option-selected]').forEach(function (el) {
            var i = parseInt(el.dataset.cdpOptionSelected, 10);
            el.textContent = v && v.options ? v.options[i] || '' : '';
          });

          var pricing = this.computePricing(v, qty);
          this.renderPrices(v, pricing);
          this.renderOfferPrices(v);
          this.renderButton(v, pricing);
          this.renderSelectedLabel(v, qty);

          if (v && v.featured_media_id) {
            var gallery = document.querySelector('cdp-gallery[data-section-id="' + this.sectionId + '"]');
            if (gallery && gallery.selectByMediaId) gallery.selectByMediaId(v.featured_media_id);
          }

          if (opts.updateUrl !== false && v && this.dataset.updateUrl !== 'false') {
            try {
              var url = new URL(window.location.href);
              url.searchParams.set('variant', v.id);
              window.history.replaceState({}, '', url.toString());
            } catch (e) {
              /* no-op */
            }
          }

          var detail = {
            sectionId: this.sectionId,
            variant: v,
            quantity: qty,
            sellingPlanId: this.planInput ? this.planInput.value : '',
            pricing: pricing,
            tiers: this.tiers,
            source: this
          };
          document.dispatchEvent(new CustomEvent('cdp:variant:change', { detail: detail }));

          /* Keep Dawn-based theme components (quantity rules, etc.) in sync. */
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            try {
              publish(PUB_SUB_EVENTS.variantChange, {
                data: { sectionId: this.sectionId, variant: v, html: document }
              });
            } catch (e) {
              /* no-op */
            }
          }
        }

        renderPrices(v, pricing) {
          var priceEl = this.querySelector('[data-cdp-price]');
          var compareEl = this.querySelector('[data-cdp-compare]');
          var saveEl = this.querySelector('[data-cdp-save]');
          var unitEl = this.querySelector('[data-cdp-unit]');

          if (!v || !pricing) {
            if (priceEl) priceEl.textContent = '—';
            if (compareEl) compareEl.hidden = true;
            if (saveEl) saveEl.hidden = true;
            if (unitEl) unitEl.hidden = true;
            return;
          }

          if (priceEl) priceEl.textContent = money(pricing.total, this);
          if (compareEl) {
            compareEl.hidden = !pricing.onSale;
            compareEl.textContent = pricing.onSale ? money(pricing.compare, this) : '';
          }
          if (saveEl) {
            saveEl.hidden = !pricing.onSale;
            if (pricing.onSale) {
              saveEl.textContent = (saveEl.dataset.template || 'Save [percent]%').replace('[percent]', pricing.savePct);
            }
          }
          if (unitEl) {
            var showUnit = pricing.multiplier > 1;
            unitEl.hidden = !showUnit;
            if (showUnit) {
              unitEl.textContent = (unitEl.dataset.template || '[price] each').replace('[price]', money(pricing.unit, this));
            }
          }
        }

        /* Each tier shows real money: unit price x quantity, less that tier's
           discount, against the real compare-at price x quantity. */
        renderOfferPrices(v) {
          if (!v) return;
          var self = this;
          var list = v.compare_at_price && v.compare_at_price > v.price ? v.compare_at_price : v.price;
          this.querySelectorAll('[data-cdp-offer]').forEach(function (input) {
            var qty = parseInt(input.dataset.quantity, 10) || 1;
            var d = parseFloat(input.dataset.discount) || 0;
            var wrap = input.closest('.cdp-offer');
            if (!wrap) return;
            var total = applyDiscount(v.price * qty, d);
            var compare = list * qty;
            var p = wrap.querySelector('[data-cdp-offer-price]');
            var c = wrap.querySelector('[data-cdp-offer-compare]');
            var s = wrap.querySelector('[data-cdp-offer-save]');
            if (p) p.textContent = money(total, self);
            if (c) {
              c.hidden = !(compare > total);
              if (compare > total) c.textContent = money(compare, self);
            }
            if (s) {
              s.hidden = !(compare > total);
              if (compare > total) {
                s.textContent = (s.dataset.template || 'Save [percent]%').replace(
                  '[percent]',
                  Math.round(((compare - total) / compare) * 100)
                );
              }
            }
          });
        }

        renderButton(v, pricing) {
          var btn = this.querySelector('[data-cdp-atc]');
          if (!btn) return;
          var label = btn.querySelector('[data-cdp-atc-label]');
          var strings = {
            add: btn.dataset.labelAdd || 'Add to cart',
            soldOut: btn.dataset.labelSoldOut || 'Sold out',
            unavailable: btn.dataset.labelUnavailable || 'Unavailable'
          };
          var text;
          if (!v) {
            text = strings.unavailable;
            btn.setAttribute('disabled', 'disabled');
          } else if (!v.available) {
            text = strings.soldOut;
            btn.setAttribute('disabled', 'disabled');
          } else {
            text = strings.add;
            btn.removeAttribute('disabled');
          }
          if (btn.dataset.showPrice === 'true' && v && v.available && pricing) {
            text = text + ' — ' + money(pricing.total, this);
          }
          if (label) label.textContent = text;
        }

        renderSelectedLabel(v, qty) {
          var els = this.querySelectorAll('[data-cdp-selected-label]');
          var stickyEls = document.querySelectorAll('cdp-sticky-atc [data-cdp-selected-label]');
          if (!els.length && !stickyEls.length) return;
          var parts = [];
          var offer = this.querySelector('[data-cdp-offer]:checked');
          if (offer && offer.dataset.label) parts.push(offer.dataset.label);
          else if (qty > 1) parts.push('x' + qty);
          if (v && v.title && v.title !== 'Default Title') parts.push(v.title);
          var plan = this.querySelector('[data-cdp-selling-plan-input]:checked');
          if (plan && plan.dataset.label && plan.value) parts.push(plan.dataset.label);
          var text = parts.join(' · ');
          els.forEach(function (el) {
            el.textContent = text;
          });
          stickyEls.forEach(function (el) {
            el.textContent = text;
          });
        }

        /* -------------------------------------------------------------- cart */

        addToCart() {
          var btn = this.querySelector('[data-cdp-atc]');
          if (btn && !btn.disabled) btn.click();
        }
      }
    );
  }

  /* ============================================================ <cdp-gallery>
     Desktop: thumbnail rail + single stage. Mobile: native scroll-snap rail.
     ========================================================================= */
  if (!customElements.get('cdp-gallery')) {
    customElements.define(
      'cdp-gallery',
      class CdpGallery extends HTMLElement {
        connectedCallback() {
          this.viewport = this.querySelector('.cdp-gallery__viewport');
          this.items = Array.prototype.slice.call(this.querySelectorAll('.cdp-gallery__item'));
          this.thumbs = Array.prototype.slice.call(this.querySelectorAll('.cdp-gallery__thumb'));
          this.dots = Array.prototype.slice.call(this.querySelectorAll('.cdp-gallery__dot'));
          this.mq = window.matchMedia('(max-width: 749px)');
          this.index = 0;

          this._onThumb = this.onThumbClick.bind(this);
          this.addEventListener('click', this._onThumb);

          this._onScroll = this.onScroll.bind(this);
          if (this.viewport) this.viewport.addEventListener('scroll', this._onScroll, { passive: true });

          this._onMq = this.applyMode.bind(this);
          if (this.mq.addEventListener) this.mq.addEventListener('change', this._onMq);

          var self = this;
          this._onBlockSelect = function (e) {
            for (var i = 0; i < self.items.length; i++) {
              if (self.items[i].contains(e.target)) {
                self.select(i, true);
                return;
              }
            }
          };
          document.addEventListener('shopify:block:select', this._onBlockSelect);

          this.applyMode();
          this.select(0);
        }

        disconnectedCallback() {
          this.removeEventListener('click', this._onThumb);
          if (this.viewport) this.viewport.removeEventListener('scroll', this._onScroll);
          if (this.mq && this.mq.removeEventListener) this.mq.removeEventListener('change', this._onMq);
          document.removeEventListener('shopify:block:select', this._onBlockSelect);
        }

        get isMobile() {
          return this.mq.matches;
        }

        applyMode() {
          if (this.isMobile) {
            this.items.forEach(function (i) {
              i.hidden = false;
            });
          } else {
            this.select(this.index || 0);
          }
        }

        onThumbClick(evt) {
          var thumb = evt.target.closest ? evt.target.closest('.cdp-gallery__thumb, .cdp-gallery__dot') : null;
          if (!thumb) return;
          evt.preventDefault();
          this.select(parseInt(thumb.dataset.index, 10) || 0, true);
        }

        onScroll() {
          if (!this.isMobile || !this.viewport) return;
          var w = this.viewport.clientWidth || 1;
          var idx = Math.round(this.viewport.scrollLeft / w);
          this.setActiveIndicators(idx);
          this.index = idx;
        }

        setActiveIndicators(index) {
          this.thumbs.forEach(function (t, i) {
            t.setAttribute('aria-current', i === index ? 'true' : 'false');
          });
          this.dots.forEach(function (d, i) {
            d.setAttribute('aria-current', i === index ? 'true' : 'false');
          });
        }

        select(index, userInitiated) {
          if (index < 0 || index >= this.items.length) return;
          this.index = index;
          this.setActiveIndicators(index);

          if (this.isMobile) {
            if (this.viewport && userInitiated) {
              this.viewport.scrollTo({ left: this.viewport.clientWidth * index, behavior: 'smooth' });
            }
            return;
          }
          this.items.forEach(function (item, i) {
            item.hidden = i !== index;
            var media = item.querySelector('video');
            if (media && i !== index && !media.paused) media.pause();
          });
        }

        selectByMediaId(mediaId) {
          for (var i = 0; i < this.items.length; i++) {
            if (String(this.items[i].dataset.mediaId) === String(mediaId)) {
              this.select(i, true);
              return;
            }
          }
        }
      }
    );
  }

  /* ========================================================= <cdp-sticky-atc>
     Reveals after the main buy area scrolls out of view; mirrors main state.
     ========================================================================= */
  if (!customElements.get('cdp-sticky-atc')) {
    customElements.define(
      'cdp-sticky-atc',
      class CdpStickyAtc extends HTMLElement {
        connectedCallback() {
          this.bar = this.querySelector('.cdp-sticky');
          this.sectionId = this.dataset.sectionId;

          this._onVariant = this.onVariant.bind(this);
          document.addEventListener('cdp:variant:change', this._onVariant);

          this._onClick = this.onClick.bind(this);
          this.addEventListener('click', this._onClick);

          var anchor = document.querySelector('[data-cdp-sticky-anchor="' + this.sectionId + '"]');
          var self = this;
          if (anchor && 'IntersectionObserver' in window) {
            this.observer = new IntersectionObserver(
              function (entries) {
                var e = entries[0];
                var show = !e.isIntersecting && e.boundingClientRect.top < 0;
                self.toggle(show);
              },
              { threshold: 0 }
            );
            this.observer.observe(anchor);
          } else {
            this._onScroll = function () {
              self.toggle((window.scrollY || 0) > 700);
            };
            window.addEventListener('scroll', this._onScroll, { passive: true });
          }

          if (window.Shopify && window.Shopify.designMode) this.toggle(true);
        }

        disconnectedCallback() {
          document.removeEventListener('cdp:variant:change', this._onVariant);
          this.removeEventListener('click', this._onClick);
          if (this.observer) this.observer.disconnect();
          if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
        }

        toggle(show) {
          if (this.bar) this.bar.classList.toggle('is-visible', !!show);
        }

        onClick(evt) {
          var btn = evt.target.closest ? evt.target.closest('[data-cdp-sticky-add]') : null;
          if (!btn) return;
          evt.preventDefault();
          document.dispatchEvent(new CustomEvent('cdp:request', { detail: { action: 'add' } }));
        }

        onVariant(evt) {
          var d = evt.detail;
          if (this.sectionId && d.sectionId !== this.sectionId) return;
          var v = d.variant;
          var pricing = d.pricing;
          var priceEl = this.querySelector('[data-cdp-sticky-price]');
          var cmpEl = this.querySelector('[data-cdp-sticky-compare]');
          var btn = this.querySelector('[data-cdp-sticky-add]');

          if (priceEl) priceEl.textContent = pricing ? money(pricing.total, this) : '—';
          if (cmpEl) {
            cmpEl.hidden = !(pricing && pricing.onSale);
            if (!cmpEl.hidden) cmpEl.textContent = money(pricing.compare, this);
          }
          if (btn) {
            var label = btn.querySelector('[data-cdp-atc-label]');
            if (!v || !v.available) {
              btn.setAttribute('disabled', 'disabled');
              if (label) label.textContent = btn.dataset.labelSoldOut || 'Sold out';
            } else {
              btn.removeAttribute('disabled');
              if (label) label.textContent = btn.dataset.labelAdd || 'Add to cart';
            }
          }
          var thumb = this.querySelector('[data-cdp-sticky-thumb]');
          if (thumb && v && v.featured_image) {
            thumb.src = v.featured_image;
            thumb.srcset = '';
          }
        }
      }
    );
  }

  /* ============================================================ <cdp-cta-buy>
     Final CTA: proxies to the main product section - never its own state.
     ========================================================================= */
  if (!customElements.get('cdp-cta-buy')) {
    customElements.define(
      'cdp-cta-buy',
      class CdpCtaBuy extends HTMLElement {
        connectedCallback() {
          this._onClick = this.onClick.bind(this);
          this.addEventListener('click', this._onClick);
          this._onVariant = this.onVariant.bind(this);
          document.addEventListener('cdp:variant:change', this._onVariant);
          this.hasMain = !!document.querySelector('cdp-product');
          if (!this.hasMain) {
            var add = this.querySelector('[data-cdp-cta-add]');
            var tiers = this.querySelector('.cdp-cta__tiers');
            if (tiers) tiers.hidden = true;
            if (add) add.setAttribute('disabled', 'disabled');
          }
        }

        disconnectedCallback() {
          this.removeEventListener('click', this._onClick);
          document.removeEventListener('cdp:variant:change', this._onVariant);
        }

        onClick(evt) {
          var tier = evt.target.closest ? evt.target.closest('[data-cdp-cta-tier]') : null;
          if (tier) {
            evt.preventDefault();
            this.querySelectorAll('[data-cdp-cta-tier]').forEach(function (b) {
              b.setAttribute('aria-pressed', b === tier ? 'true' : 'false');
            });
            document.dispatchEvent(
              new CustomEvent('cdp:request', { detail: { quantity: parseInt(tier.dataset.quantity, 10) || 1 } })
            );
            return;
          }
          var add = evt.target.closest ? evt.target.closest('[data-cdp-cta-add]') : null;
          if (add && this.hasMain) {
            evt.preventDefault();
            document.dispatchEvent(new CustomEvent('cdp:request', { detail: { action: 'add' } }));
          }
        }

        onVariant(evt) {
          var d = evt.detail;
          var v = d.variant;
          var pricing = d.pricing;
          var priceEl = this.querySelector('[data-cdp-cta-price]');
          var cmpEl = this.querySelector('[data-cdp-cta-compare]');
          var saveEl = this.querySelector('[data-cdp-cta-save]');

          if (priceEl) priceEl.textContent = pricing ? money(pricing.total, this) : '—';
          if (cmpEl) {
            cmpEl.hidden = !(pricing && pricing.onSale);
            if (!cmpEl.hidden) cmpEl.textContent = money(pricing.compare, this);
          }
          if (saveEl) {
            saveEl.hidden = !(pricing && pricing.onSale);
            if (!saveEl.hidden) {
              saveEl.textContent = (saveEl.dataset.template || 'Save [percent]%').replace('[percent]', pricing.savePct);
            }
          }
          var tiers = d.tiers || [];
          this.querySelectorAll('[data-cdp-cta-tier]').forEach(function (b) {
            var qty = parseInt(b.dataset.quantity, 10) || 1;
            b.setAttribute('aria-pressed', qty === d.quantity ? 'true' : 'false');
            var p = b.querySelector('[data-cdp-cta-tier-price]');
            if (!p || !v) return;
            var match = tiers.filter(function (x) {
              return x.quantity === qty;
            })[0];
            p.textContent = money(applyDiscount(v.price * qty, match ? match.discount : 0), b);
          });
          var addBtn = this.querySelector('[data-cdp-cta-add]');
          if (addBtn && addBtn.tagName === 'BUTTON') {
            if (!v || !v.available) addBtn.setAttribute('disabled', 'disabled');
            else addBtn.removeAttribute('disabled');
          }
        }
      }
    );
  }

  /* ========================================================== <cdp-accordion>
     FAQ. One item open at a time by default; keyboard + a11y friendly.
     ========================================================================= */
  if (!customElements.get('cdp-accordion')) {
    customElements.define(
      'cdp-accordion',
      class CdpAccordion extends HTMLElement {
        connectedCallback() {
          this.single = this.dataset.single !== 'false';
          this._onClick = this.onClick.bind(this);
          this.addEventListener('click', this._onClick);
          var self = this;
          this._onBlockSelect = function (e) {
            var btn = e.target && e.target.querySelector ? e.target.querySelector('.cdp-faq__q') : null;
            if (btn && self.contains(btn)) self.open(btn);
          };
          document.addEventListener('shopify:block:select', this._onBlockSelect);
        }

        disconnectedCallback() {
          this.removeEventListener('click', this._onClick);
          document.removeEventListener('shopify:block:select', this._onBlockSelect);
        }

        onClick(evt) {
          var btn = evt.target.closest ? evt.target.closest('.cdp-faq__q') : null;
          if (!btn || !this.contains(btn)) return;
          evt.preventDefault();
          if (btn.getAttribute('aria-expanded') === 'true') this.close(btn);
          else this.open(btn);
        }

        open(btn) {
          var self = this;
          if (this.single) {
            this.querySelectorAll('.cdp-faq__q[aria-expanded="true"]').forEach(function (other) {
              if (other !== btn) self.close(other);
            });
          }
          btn.setAttribute('aria-expanded', 'true');
          var panel = document.getElementById(btn.getAttribute('aria-controls'));
          if (panel) panel.hidden = false;
        }

        close(btn) {
          btn.setAttribute('aria-expanded', 'false');
          var panel = document.getElementById(btn.getAttribute('aria-controls'));
          if (panel) panel.hidden = true;
        }
      }
    );
  }

  /* ========================================================= <cdp-disclosure>
     Generic show/hide (full ingredient list, "load more", etc.)
     ========================================================================= */
  if (!customElements.get('cdp-disclosure')) {
    customElements.define(
      'cdp-disclosure',
      class CdpDisclosure extends HTMLElement {
        connectedCallback() {
          this._onClick = this.onClick.bind(this);
          this.addEventListener('click', this._onClick);
        }
        disconnectedCallback() {
          this.removeEventListener('click', this._onClick);
        }
        onClick(evt) {
          var btn = evt.target.closest ? evt.target.closest('[data-cdp-toggle]') : null;
          if (!btn) return;
          evt.preventDefault();
          var panel = document.getElementById(btn.getAttribute('aria-controls'));
          if (!panel) return;
          var open = btn.getAttribute('aria-expanded') === 'true';
          btn.setAttribute('aria-expanded', String(!open));
          panel.hidden = open;
          if (btn.dataset.labelOpen && btn.dataset.labelClosed) {
            var label = btn.querySelector('[data-cdp-toggle-label]') || btn;
            label.textContent = open ? btn.dataset.labelClosed : btn.dataset.labelOpen;
          }
        }
      }
    );
  }

  /* ===================================================== <cdp-compare-slider>
     Before / after drag comparison built on a range input (keyboard + touch).
     ========================================================================= */
  if (!customElements.get('cdp-compare-slider')) {
    customElements.define(
      'cdp-compare-slider',
      class CdpCompareSlider extends HTMLElement {
        connectedCallback() {
          this.range = this.querySelector('.cdp-ba__range');
          this.wrap = this.querySelector('.cdp-ba');
          if (!this.range || !this.wrap) return;
          var self = this;
          this._onInput = function () {
            self.wrap.style.setProperty('--cdp-ba-pos', self.range.value + '%');
          };
          this.range.addEventListener('input', this._onInput);
          this._onInput();
        }
        disconnectedCallback() {
          if (this.range && this._onInput) this.range.removeEventListener('input', this._onInput);
        }
      }
    );
  }

  /* =========================================================== <cdp-carousel>
     Progressive enhancement for scroll-snap rails (prev/next + block select).
     ========================================================================= */
  if (!customElements.get('cdp-carousel')) {
    customElements.define(
      'cdp-carousel',
      class CdpCarousel extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('[data-cdp-track]');
          if (!this.track) return;
          this._onClick = this.onClick.bind(this);
          this.addEventListener('click', this._onClick);
          var self = this;
          this._onBlockSelect = function (e) {
            if (self.contains(e.target) && e.target.scrollIntoView) {
              e.target.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
            }
          };
          document.addEventListener('shopify:block:select', this._onBlockSelect);
        }
        disconnectedCallback() {
          this.removeEventListener('click', this._onClick);
          document.removeEventListener('shopify:block:select', this._onBlockSelect);
        }
        onClick(evt) {
          var nav = evt.target.closest ? evt.target.closest('[data-cdp-nav]') : null;
          if (!nav) return;
          evt.preventDefault();
          var dir = nav.dataset.cdpNav === 'next' ? 1 : -1;
          var first = this.track.firstElementChild;
          var step = first ? first.getBoundingClientRect().width + 16 : this.track.clientWidth;
          this.track.scrollBy({ left: step * dir, behavior: 'smooth' });
        }
      }
    );
  }
})();
